/**
 * Presentation shared by both clients.
 *
 * A set list renders identically in a WhatsApp reply and in the dashboard's day list,
 * and it has to: the dashboard exists to check what the bot said against what was
 * stored (D-57), and that comparison is worthless if the two spell "3×10 @ 60kg"
 * differently. Same reasoning as scoring (D-09) — one implementation, imported twice.
 *
 * Pure, like everything else here. No clock, no locale beyond an explicit "en-US".
 */
/** Thousands separators, matching the spec's "6,420". */
export function formatNumber(value) {
    return Math.round(value).toLocaleString("en-US");
}
/** Weights render as typed: "60", "72.5". Never "60.0". */
export function formatWeight(kg) {
    return String(kg);
}
export function plural(n, word) {
    return n === 1 ? word : `${word}s`;
}
/**
 * Below this many seconds a duration set is a hold and reads in seconds; at or above it
 * the set is a cardio block and reads in minutes (D-88). The number decides, not the
 * exercise — the catalog says which entries are duration-measured but not which are
 * cardio, and it should not have to: a 45-second treadmill burst is still seconds and a
 * five-minute plank is still five minutes.
 */
const MINUTES_FROM = 120;
/** "20m", "20.5m" — one decimal, and never "20.0m". */
function formatMinutes(seconds) {
    return `${String(Math.round((seconds / 60) * 10) / 10)}m`;
}
/**
 * A compact description of a set list: "3×10 @ 60kg", "10,8,6 @ 60kg", "3×60s",
 * "30m total". Collapses to the ×-form only when every set is genuinely identical.
 */
export function formatSets(exercise, sets) {
    if (sets.length === 0)
        return "no sets";
    const unit = exercise.measure === "duration" ? "s" : "";
    const first = sets[0];
    const uniform = sets.every((s) => s.reps === first.reps && s.weightKg === first.weightKg);
    /*
     * A minutes-scale block is summed rather than listed: what you want off a cardio line
     * is how long you were on the machine, and "2×900s" is neither of the two numbers you
     * would have asked for. Every set has to be minutes-scale — one short set among long
     * ones is a different kind of effort and the list says so — and the weights have to
     * match, because a sum cannot carry two of them (a loaded sled block falls through to
     * the per-set form below, which can).
     */
    if (exercise.measure === "duration" &&
        sets.every((s) => s.reps >= MINUTES_FROM && s.weightKg === first.weightKg)) {
        const total = formatMinutes(sets.reduce((sum, s) => sum + s.reps, 0));
        const scope = sets.length > 1 ? `${total} total` : total;
        return first.weightKg ? `${scope} @ ${formatWeight(first.weightKg)}kg` : scope;
    }
    const reps = uniform
        ? `${sets.length}×${first.reps}${unit}`
        : sets.map((s) => `${s.reps}${unit}`).join(",");
    const weights = new Set(sets.map((s) => s.weightKg));
    if (weights.size === 1 && first.weightKg === 0)
        return reps;
    if (weights.size === 1)
        return `${reps} @ ${formatWeight(first.weightKg)}kg`;
    // Mixed weights cannot collapse, so spell each set out in full.
    return sets
        .map((s) => `${s.reps}${unit}${s.weightKg ? ` @ ${formatWeight(s.weightKg)}kg` : ""}`)
        .join(", ");
}
//# sourceMappingURL=format.js.map